import { presentationApi } from '../../api/presentation.js';
import { harmoniserPresentations } from '../presentation/horsLigne/index.js';
import type { CorpsInteretPresentation } from '../presentation/horsLigne/source.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cleMetier } from '../../api/cles.js';
import { authApi } from '../../api/auth.js';
import { contactsApi } from '../../api/contacts.js';
import type { CorpsDemande } from '../../api/demandes.js';
import { demandesApi } from '../../api/demandes.js';
import { oeuvresApi } from '../../api/oeuvres.js';
import type { AppelApi } from '../../api/client.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { reservationsApi } from '../../api/reservations.js';
import { offresApi } from '../../api/offres.js';
import { ventesApi } from '../../api/ventes.js';
import { oublierIdentite, useIdentite } from '../../auth/identite.js';
import { CLE_SESSION, queryClient } from '../../auth/queryClient.js';
import { purgerSiDroitsChanges } from '../../auth/session.js';
import {
  annoncerConfinement,
  estConfinee,
  estRefusConfinement,
  noterConfinement,
} from '../../auth/confinement.js';
import { estRefusCgu } from '../../auth/cgu.js';
import type { SessionUtilisateur } from '../../api/types.js';
import {
  empilerActionFoire,
  fileEnMemoire,
  fusionnerCorpsActionFoire,
  lireFileHorsLigne,
  lireQuarantaine,
  migrerAncienneFile,
  operationDe,
  rejouerFileHorsLigne,
  ActionAVerifier,
  ArretRejeu,
  type ActionFoireEnAttente,
  type BilanRejeu,
  type EtapeRejeu,
  type RaisonArretRejeu,
  type PorteeFile,
  type TypeActionFoire,
} from './fileHorsLigne.js';

function estEnLigne(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine;
}

/**
 * Cible d'un geste qui a besoin d'un contact (Réserver, Vendre) : soit un
 * contact déjà choisi dans le carnet, soit une saisie rapide nom/e-mail à
 * transformer en contact avant de poursuivre. `contactIdResolu` est gravé
 * dans la file dès que la création réussit (`fusionnerCorpsActionFoire`),
 * pour qu'un rejeu après échec de l'étape suivante ne recrée pas le contact.
 */
export interface CibleContactFoire {
  contactId?: string;
  rapide?: { nom: string; email: string };
  contactIdResolu?: string;
}

export interface CorpsReservationFoire {
  contact: CibleContactFoire;
  oeuvreId: string;
  exemplaireId?: string | null;
  jusquau: string;
  note?: string;
}

export interface CorpsVenteFoire {
  contact: CibleContactFoire;
  /** Le champ de prix est celui de `ligneEntreeSchema` (API) : `prixUnitaireCentimes`. */
  lignes: {
    oeuvreId: string;
    /**
     * L'exemplaire vendu d'une édition suivie (seconde relecture ARTO-51, N7) :
     * gardé dans la file, pour qu'un rejeu hors ligne vende le même.
     */
    exemplaireId?: string;
    designation: string;
    /**
     * Le prix affiché au stand — celui de l'exemplaire choisi, sinon de
     * l'œuvre —, conservé pour le rejeu. Absent si l'œuvre n'en a pas : l'API
     * le dit alors, au lieu de vendre à 0 €.
     */
    prixUnitaireCentimes?: number;
  }[];
  /** Présent seulement si l'utilisateur a demandé l'émission immédiate. */
  /** Date CIVILE `AAAA-MM-JJ` (`dateCivileSchema`), jamais un horodatage ISO. */
  emettre?: { emiseLe: string };
  /**
   * Le brouillon déjà créé pour cette vente, gravé dans la file AVANT
   * l'émission : un rejeu après un échec d'émission émet ce brouillon-là au
   * lieu d'en créer un second (ARTO-52).
   */
  factureIdBrouillon?: string;
  /**
   * Version du brouillon capturée à sa création (ARTO-51/52) : l'émission
   * l'envoie en `versionAttendue`, et l'API refuse d'émettre un brouillon
   * modifié depuis (`409 brouillon_modifie`).
   */
  versionBrouillon?: number;
}

export interface CorpsOffreFoire {
  contact: CibleContactFoire;
  oeuvreId: string;
  exemplaireId?: string;
  prixProposeCentimes: number;
  validiteJusquau: string;
  expositionId?: string;
  message?: string;
}

/** Version d'un brouillon que l'API vient de créer (contrat d'ARTO-52). */
const VERSION_DE_CREATION = 0;

/**
 * Un 409, à N'IMPORTE QUELLE étape (troisième relecture du 23/09, constat N6),
 * dit que le serveur a changé sous l'action : même opération au contenu
 * différent (`idempotency_conflit`), brouillon modifié, facture déjà émise,
 * œuvre déjà réservée ou vendue… Rien ne se résoudra en réessayant : l'action
 * est « à vérifier en régie ». Seul `compte_inattendu` arrête le rejeu (la
 * session a changé de compte), traité par `arretSi`.
 */
/**
 * Les refus métier d'ARTO-51 que rien ne résoudra en réessayant, quel que
 * soit leur statut : l'œuvre est réservée pour un autre contact, vendue en
 * quantité unique, suivie exemplaire par exemplaire, ou son type est
 * verrouillé. Une personne doit trancher en régie.
 */
const RAISONS_A_VERIFIER = new Set([
  'reservee_pour_un_autre',
  'quantite_unitaire',
  'exemplaire_requis',
  'type_verrouille',
]);

function aVerifierSi(erreur: unknown): void {
  if (!estApiError(erreur)) return;
  const raison = (erreur.details as { raison?: unknown } | null)?.raison;
  if (erreur.status === 409 || (typeof raison === 'string' && RAISONS_A_VERIFIER.has(raison))) {
    throw new ActionAVerifier(`À vérifier en régie : ${erreur.message}`);
  }
}

/**
 * L'appel d'une étape réseau, transmis EXPLICITEMENT jusqu'au `fetch` : le
 * signal de l'époque du rejeu, le compte de l'action (`X-Artosera-Compte-Attendu`)
 * et sa clé d'opération (`Idempotency-Key`).
 */
function operation(action: ActionFoireEnAttente, nom: string, etape: EtapeRejeu): AppelApi {
  return {
    signal: etape.signal,
    compteAttendu: action.compteId,
    cleOperation: `${operationDe(action)}-${nom}`,
  };
}

/**
 * Ce qui arrête le rejeu sans imputer d'échec à l'action (relecture du 23/09,
 * constats 2 et 7) ; toute autre erreur — dont un `409 facture.immuable`, qui
 * ne prouve RIEN de cette opération (constat 4) — marque l'action et la garde.
 */
function arretSi(erreur: unknown): never {
  if (estErreurReseau(erreur)) throw new ArretRejeu('reseau', erreur.message);
  if (estApiError(erreur)) {
    if (erreur.status === 401) {
      throw new ArretRejeu('session_expiree', 'Session expirée : reconnectez-vous.');
    }
    const raison = (erreur.details as { raison?: unknown } | null)?.raison;
    if (raison === 'compte_change') throw new ArretRejeu('compte_inattendu', erreur.message);
    // Session confinée (CGU, double authentification) : l'action n'a rien
    // d'un échec, elle attend que la session soit libérée.
    if (estRefusConfinement(erreur)) {
      annoncerConfinement();
      throw new ArretRejeu(
        estRefusCgu(erreur) ? 'cgu_a_accepter' : 'double_authentification_a_activer',
        erreur.message,
      );
    }
  }
  aVerifierSi(erreur);
  throw erreur;
}

async function etapeReseau<T>(etape: EtapeRejeu, appel: () => Promise<T>): Promise<T> {
  etape.verifier();
  try {
    return await appel();
  } catch (erreur) {
    return arretSi(erreur);
  }
}

/** La vente simple, sans facture (D21, cœur) : ce que la file garde pour le rejeu. */
export interface CorpsVenteSimpleFoire {
  contact: CibleContactFoire;
  oeuvreId: string;
  /** Le prix affiché au stand, conservé : le rejeu vend au prix convenu. */
  prixCentimes: number;
  /** Date CIVILE `AAAA-MM-JJ` du geste, pas celle du rejeu. */
  venduLe: string;
}

async function resoudreContactId(
  portee: PorteeFile,
  action: ActionFoireEnAttente,
  cible: CibleContactFoire,
  etape: EtapeRejeu,
): Promise<string> {
  if (cible.contactId) return cible.contactId;
  if (cible.contactIdResolu) return cible.contactIdResolu;
  if (!cible.rapide) throw new Error('Aucun contact fourni pour ce geste.');
  const rapide = cible.rapide;
  const { contact } = await etapeReseau(etape, () =>
    contactsApi.creer(
      action.galerieSlug,
      { nom: rapide.nom, emails: rapide.email ? [rapide.email] : [], origine: 'regie' },
      operation(action, 'contact', etape),
    ),
  );
  etape.verifier();
  fusionnerCorpsActionFoire<{ contact: CibleContactFoire }>(
    action.id,
    (corps) => ({ ...corps, contact: { ...corps.contact, contactIdResolu: contact.id } }),
    portee,
  );
  return contact.id;
}

/**
 * Exécute une action sous SA galerie et SON compte (celui qu'elle porte),
 * jamais sous l'identité ouverte au moment du rejeu. Chaque étape réseau
 * vérifie l'époque avant de partir, envoie la clé d'opération et le compte
 * attendu : l'API rend le résultat de CETTE opération si elle a déjà abouti.
 */
async function executerAction(
  portee: PorteeFile,
  action: ActionFoireEnAttente,
  etape: EtapeRejeu,
): Promise<void> {
  const galerie = action.galerieSlug;
  if (action.type === 'interet-presentation') {
    const session = queryClient.getQueryData<SessionUtilisateur>(CLE_SESSION);
    const acces = session?.galeries.find((candidate) => candidate.slug === galerie);
    if (
      !acces ||
      !galerieAModule(acces, 'rendez-vous-client') ||
      !galerieALaction(acces, 'presentation:write')
    ) {
      throw new ArretRejeu('acces_retire', 'Présentation non autorisée.');
    }
    const corps = action.corps as CorpsInteretPresentation;
    await etapeReseau(etape, () =>
      presentationApi.marquerInteret(
        galerie,
        corps.selectionId,
        corps.oeuvreId,
        { exemplaireId: corps.exemplaireId, interesse: corps.interesse },
        { signal: etape.signal, compteAttendu: action.compteId, cleOperation: operationDe(action) },
      ),
    );
    return;
  }
  if (action.type === 'reservation') {
    const corps = action.corps as CorpsReservationFoire;
    const contactId = await resoudreContactId(portee, action, corps.contact, etape);
    await etapeReseau(etape, () =>
      reservationsApi.creer(
        galerie,
        {
          oeuvreId: corps.oeuvreId,
          exemplaireId: corps.exemplaireId,
          contactId,
          jusquau: corps.jusquau,
          note: corps.note,
        },
        operation(action, 'reservation', etape),
      ),
    );
    return;
  }
  if (action.type === 'demande') {
    await etapeReseau(etape, () =>
      demandesApi.creer(galerie, action.corps as CorpsDemande, operation(action, 'demande', etape)),
    );
    return;
  }
  if (action.type === 'vente-simple') {
    // La vente simple, sans facture (D21, cœur) : même discipline que les
    // autres gestes — époque vérifiée, compte attendu, clé d'opération. Un
    // rejeu d'une vente déjà passée rend son résultat au lieu d'en refaire une.
    const corps = action.corps as CorpsVenteSimpleFoire;
    const contactId = await resoudreContactId(portee, action, corps.contact, etape);
    await etapeReseau(etape, () =>
      oeuvresApi.marquerVendue(
        galerie,
        corps.oeuvreId,
        { contactId, prixCentimes: corps.prixCentimes, venduLe: corps.venduLe },
        operation(action, 'vente-simple', etape),
      ),
    );
    return;
  }
  if (action.type === 'offre') {
    const corps = action.corps as CorpsOffreFoire;
    const contactId = await resoudreContactId(portee, action, corps.contact, etape);
    await etapeReseau(etape, () =>
      offresApi.creer(
        galerie,
        {
          contactId,
          lignes: [
            {
              oeuvreId: corps.oeuvreId,
              ...(corps.exemplaireId ? { exemplaireId: corps.exemplaireId } : {}),
              prixProposeCentimes: corps.prixProposeCentimes,
            },
          ],
          validiteJusquau: corps.validiteJusquau,
          ...(corps.expositionId ? { expositionId: corps.expositionId } : {}),
          ...(corps.message ? { message: corps.message } : {}),
          canal: 'stand',
        },
        operation(action, 'offre', etape),
      ),
    );
    return;
  }
  const corps = action.corps as CorpsVenteFoire;
  let factureId = corps.factureIdBrouillon;
  let version = corps.versionBrouillon;
  if (!factureId) {
    const contactId = await resoudreContactId(portee, action, corps.contact, etape);
    const { facture } = await etapeReseau(etape, () =>
      ventesApi.creer(
        galerie,
        { contactId, lignes: corps.lignes },
        operation(action, 'facture', etape),
      ),
    );
    etape.verifier();
    // Le stand n'adopte JAMAIS la version reçue (quatrième relecture, 1) : un
    // brouillon créé est à la version de création, et le serveur refuse le
    // rejeu d'une création dont le brouillon a bougé (`409
    // brouillon_modifie`). Une autre version reçue n'est pas celle que le
    // stand a écrite : l'action est à vérifier, pas émise.
    if (facture.version !== VERSION_DE_CREATION) {
      throw new ActionAVerifier(
        'À vérifier en régie : le brouillon créé au stand a été modifié depuis.',
      );
    }
    factureId = facture.id;
    version = VERSION_DE_CREATION;
    fusionnerCorpsActionFoire<CorpsVenteFoire>(
      action.id,
      (courant) => ({
        ...courant,
        factureIdBrouillon: facture.id,
        versionBrouillon: VERSION_DE_CREATION,
      }),
      portee,
    );
  }
  if (corps.emettre) {
    const emettre = corps.emettre;
    const id = factureId;
    // La version capturée À LA CRÉATION du brouillon, exigée par l'API
    // (ARTO-51) : un brouillon modifié depuis est refusé (`409
    // brouillon_modifie`, « à vérifier en régie »). Une action d'avant cette
    // capture n'a pas de version : elle ne peut pas prouver que le brouillon
    // est celui du stand, elle est donc à vérifier plutôt qu'émise.
    const attendue = version;
    if (attendue === undefined) {
      throw new ActionAVerifier(
        'À vérifier en régie : la version du brouillon créé au stand est inconnue.',
      );
    }
    await etapeReseau(etape, () =>
      ventesApi.emettre(
        galerie,
        id,
        { emiseLe: emettre.emiseLe, versionAttendue: attendue },
        operation(action, 'emission', etape),
      ),
    );
  }
}

/**
 * Rejoue la file du stand pour `portee` : sérialisé, cloisonné par époque,
 * idempotent côté serveur (voir `rejouerFileHorsLigne`).
 */
export function rejouerFoire(portee: PorteeFile): Promise<BilanRejeu> {
  return rejouerFileHorsLigne((action, etape) => executerAction(portee, action, etape), portee, {
    preparer: (etape) => revaliderSession(portee, etape),
  });
}

/**
 * Avant tout rejeu, la session est RELUE (relecture du 23/09, constat 7) :
 * un 401 arrête le rejeu et le signale, un autre compte ou une galerie
 * retirée aussi ; les droits frais remplacent ceux du cache.
 */
async function revaliderSession(portee: PorteeFile, etape: EtapeRejeu): Promise<void> {
  const session = await etapeReseau(etape, () => authApi.moi());
  etape.verifier();
  if (session.compte.id !== portee.compteId) {
    // Le cookie est celui d'un autre compte : l'identité affichée n'est plus
    // la bonne. Elle est oubliée — la coquille, qui lit la session, se vide.
    await oublierIdentite(queryClient);
    throw new ArretRejeu('compte_different', 'La session ouverte est celle d’un autre compte.');
  }
  // Dès cette réponse validée : le repli ne dépend pas d’une seconde lecture.
  noterConfinement(session);
  // Des droits changés purgent d'abord les données lues sous les anciens
  // (constat N7) ; puis les droits frais remplacent ceux affichés : la
  // coquille lit cette requête (constat G), même quand l'accès à la galerie
  // vient d'être retiré.
  await purgerSiDroitsChanges(
    queryClient.getQueryData<SessionUtilisateur>(CLE_SESSION),
    session,
    portee.galerieSlug,
  );
  await harmoniserPresentations(
    session,
    portee.galerieSlug,
    false,
    queryClient.getQueryData<SessionUtilisateur>(CLE_SESSION),
  );
  queryClient.setQueryData(CLE_SESSION, session);
  if (!session.galeries.some((galerie) => galerie.slug === portee.galerieSlug)) {
    throw new ArretRejeu('acces_retire', 'Ce compte n’a plus accès à cette galerie.');
  }
  // Session relue confinée (CGU à accepter, puis double authentification à
  // activer). Rien ne part — chaque envoi serait refusé —, la file reste, et
  // la coquille mène à la page qui la libère.
  if (estConfinee(session)) {
    annoncerConfinement();
    throw session.cguAcceptationRequise
      ? new ArretRejeu(
          'cgu_a_accepter',
          'Les conditions générales d’utilisation sont à accepter avant tout envoi.',
        )
      : new ArretRejeu(
          'double_authentification_a_activer',
          'La double authentification est à activer avant tout envoi.',
        );
  }
}

/**
 * Pilote la file hors ligne du mode foire (ARTO-4, 3.3 « aucune action
 * perdue silencieusement ») : expose l'état réseau, la file en attente, une
 * fonction pour empiler un geste et un rejeu automatique au retour en ligne
 * (et au montage, si des actions attendaient déjà d'un précédent passage
 * hors ligne). Réserver et Vendre enchaînent la résolution du contact (carnet
 * ou création rapide) avant leur propre appel ; un échec de l'étape suivante
 * garde le contact déjà résolu et ne retente que ce qui reste à faire.
 *
 * La file est celle du compte connecté ET de `galerie` (ARTO-52) ; au
 * montage, l'ancienne file globale est migrée et ce qu'elle contenait
 * d'inattribuable, mis en quarantaine, est compté dans `actionsEnQuarantaine`.
 *
 * `stockageMemoire` dit que le stockage local a refusé d'écrire (quota,
 * navigation privée) : la file ne survit alors qu'à l'onglet ouvert, et le
 * stand doit l'annoncer.
 */
export function useFileHorsLigneFoire(galerie: string): {
  enLigne: boolean;
  enAttente: readonly ActionFoireEnAttente[];
  /** Actions de l'ancienne file globale mises en quarantaine (jamais rejouées). */
  actionsEnQuarantaine: number;
  stockageMemoire: boolean;
  /**
   * Pourquoi le dernier rejeu s'est arrêté sans rien envoyer, quand le stand
   * doit le dire (session expirée, autre compte, accès retiré) ; `null`
   * sinon — une coupure réseau se lit déjà sur l'état en ligne.
   */
  arretRejeu: RaisonArretRejeu | null;
  /** Actions que le serveur a vues changer : à vérifier en régie, plus rejouées. */
  actionsAVerifier: number;
  empiler: (type: TypeActionFoire, corps: unknown) => ActionFoireEnAttente;
  rejouer: () => Promise<void>;
} {
  const queryClient = useQueryClient();
  const compteId = useIdentite()?.compteId ?? null;
  const portee = useMemo<PorteeFile | null>(
    () => (compteId ? { compteId, galerieSlug: galerie } : null),
    [compteId, galerie],
  );

  const [enLigne, setEnLigne] = useState(estEnLigne);
  const [actionsEnQuarantaine, setActionsEnQuarantaine] = useState(() => lireQuarantaine().length);
  const [enAttente, setEnAttente] = useState<readonly ActionFoireEnAttente[]>(() =>
    lireFileHorsLigne(portee),
  );
  const [stockageMemoire, setStockageMemoire] = useState(() => fileEnMemoire(portee));
  const [arretRejeu, setArretRejeu] = useState<RaisonArretRejeu | null>(null);

  const rafraichir = useCallback(() => {
    setEnAttente(lireFileHorsLigne(portee));
    setStockageMemoire(fileEnMemoire(portee));
  }, [portee]);

  const rejouer = useCallback(async () => {
    if (!portee || !estEnLigne()) return;
    const resultat = await rejouerFoire(portee);
    setArretRejeu(
      resultat.arret === 'reseau' || resultat.arret === 'identite_changee' ? null : resultat.arret,
    );
    rafraichir();
    if (resultat.reussies > 0) {
      // Une réservation ou une vente peut avoir changé le statut de l'œuvre
      // (réservée/vendue) ou le carnet (contact créé) : la grille et la
      // fiche doivent le refléter sans recharger la page.
      void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres') });
      void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'contacts') });
      void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'offres') });
      void queryClient.invalidateQueries({ queryKey: cleMetier(galerie, 'presentation') });
    }
  }, [galerie, portee, queryClient, rafraichir]);

  useEffect(() => {
    if (!portee) return undefined;
    // Migration différée (pas de setState synchrone dans le corps de
    // l'effet), avant le premier rejeu : l'ancienne file globale n'est
    // jamais rejouée telle quelle, et ce qu'elle avait d'inattribuable reste
    // en quarantaine — annoncé par la grille ET par la fiche.
    const identifiant = window.setTimeout(() => {
      migrerAncienneFile(portee);
      setActionsEnQuarantaine(lireQuarantaine().length);
      rafraichir();
      if (estEnLigne()) void rejouer();
    }, 0);
    return () => window.clearTimeout(identifiant);
  }, [portee, rafraichir, rejouer]);

  useEffect(() => {
    const surRetourEnLigne = (): void => {
      setEnLigne(true);
      void rejouer();
    };
    const surPerteReseau = (): void => setEnLigne(false);
    window.addEventListener('online', surRetourEnLigne);
    window.addEventListener('offline', surPerteReseau);
    return () => {
      window.removeEventListener('online', surRetourEnLigne);
      window.removeEventListener('offline', surPerteReseau);
    };
  }, [rejouer]);

  const empiler = useCallback(
    (type: TypeActionFoire, corps: unknown): ActionFoireEnAttente => {
      const action = empilerActionFoire(type, corps, portee);
      rafraichir();
      return action;
    },
    [portee, rafraichir],
  );

  return {
    enLigne,
    enAttente,
    actionsEnQuarantaine,
    stockageMemoire,
    arretRejeu,
    actionsAVerifier: enAttente.filter((action) => action.aVerifier).length,
    empiler,
    rejouer,
  };
}
